import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Share2, Mail } from "lucide-react";
import { BRAND, RESORT, CASA } from "@/lib/media";
import { useLang } from "@/context/LanguageContext";
import { useSeo } from "@/lib/seo";

const EASE = [0.22, 1, 0.36, 1];

const SHARE_TEXT = "Discover a new standard of luxury. View this exclusive residence at Derron Luxury.";

const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, ease: EASE, delay }} className="block">
      {children}
    </motion.span>
  </span>
);

function PropertyCard({ image, tag, name, location, cta, comingSoon, onClick, path, testid, delay }) {
  const { t } = useLang();
  const shareResidence = async (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}${path}`;
    const data = { title: `${name} · Derron Luxury`, text: SHARE_TEXT, url };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else {
        await navigator.clipboard.writeText(`${SHARE_TEXT} ${url}`);
        toast.success(t("Link copied — share the residence anywhere."));
      }
    } catch (_) { /* share dismissed */ }
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay }}
      className="group relative overflow-hidden rounded-sm w-full aspect-[4/5] sm:aspect-[3/4]">
      <button onClick={onClick} data-testid={testid} className="absolute inset-0 w-full h-full text-left">
        <img src={image} alt={name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/10" />
        <div className="absolute bottom-0 left-0 right-0 p-7 lg:p-9">
          <span className="overline text-white/70">{t(tag)}</span>
          <h3 className="font-display text-3xl lg:text-4xl text-white mt-2 leading-tight">{t(name)}</h3>
          <p className="text-white/75 font-light mt-1">{t(location)}</p>
          <span className="inline-flex items-center gap-2 mt-6 text-white border-b border-white/40 pb-1 group-hover:gap-4 group-hover:border-[#B86B52] transition-all duration-300">
            {t(cta)} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </span>
        </div>
      </button>
      {comingSoon && (
        <span className="absolute top-5 right-5 overline text-white bg-[#B86B52]/90 px-3 py-1.5 rounded-sm pointer-events-none">{t("Coming Soon")}</span>
      )}
      <button onClick={shareResidence} data-testid={`${testid}-share`} aria-label={`Share ${name}`}
        className="absolute top-5 left-5 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-md border border-white/25 text-white hover:bg-white hover:text-[#0F2537] transition-colors">
        <Share2 className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </motion.div>
  );
}

export default function DerronLanding() {
  const nav = useNavigate();
  const { t, lang, toggle } = useLang();
  useSeo({
    title: "Derron Luxury · Private Residences in Baja California Sur",
    description: "A curated collection of private luxury vacation homes in Los Cabos — Punto Nima in San José del Cabo and Casa de León in Cabo San Lucas. Book directly with your host.",
    path: "/",
  });
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="relative w-full bg-[#F6F4F0]">
      {/* ---- HERO ---- */}
      <div className="relative min-h-screen w-full overflow-hidden noise">
        <div className="absolute inset-0">
          <img src={BRAND.derronHero} alt="Baja California Sur luxury coastline" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0F2537]/55" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F2537]/40 via-transparent to-[#0F2537]/80" />
        </div>

        <div className="relative z-10 min-h-screen flex flex-col">
          <motion.header
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}
            className="px-6 lg:px-12 py-8 flex items-center justify-between">
            <div data-testid="derron-brand">
              <div className="font-display text-2xl lg:text-3xl tracking-[0.15em] text-[#F6F4F0]">DERRON LUXURY</div>
              <div className="text-[#F6F4F0]/60 text-[10px] tracking-[0.35em] uppercase mt-0.5">Baja California Sur</div>
            </div>
            <div className="flex items-center gap-6">
              <button data-testid="landing-owner-link" onClick={() => document.getElementById("owner")?.scrollIntoView({ behavior: "smooth" })}
                className="hidden sm:block overline text-[#F6F4F0]/70 hover:text-[#E4B58C] transition-colors">{t("Meet Florencia")}</button>
              <button data-testid="landing-lang-toggle" onClick={toggle} aria-label="Switch language"
                className="text-xs tracking-[0.15em] font-medium text-[#F6F4F0]/80 hover:text-[#E4B58C] transition-colors">
                <span className={lang === "en" ? "text-[#E4B58C]" : ""}>EN</span>
                <span className="mx-1 opacity-40">/</span>
                <span className={lang === "es" ? "text-[#E4B58C]" : ""}>ES</span>
              </button>
            </div>
          </motion.header>

          <div className="flex-1 flex flex-col justify-center px-6 lg:px-12 max-w-[1400px] mx-auto w-full pt-8 pb-16">
            <span className="overline text-[#E4B58C] mb-6">{t("The Derron Luxury Collection")}</span>
            <h1 className="font-display font-light text-white text-5xl sm:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight max-w-4xl">
              <MaskLine delay={0.2}>{t("Live the Baja")}</MaskLine>
              <MaskLine delay={0.38}><span className="italic">{t("you've always")}</span> {t("imagined.")}</MaskLine>
            </h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 1 }}
              className="text-white/85 font-light text-lg max-w-xl mt-8 leading-relaxed">
              {t("A curated portfolio of private coastal residences across Baja California Sur — where every stay is effortless, intimate and unforgettable. Choose your escape.")}
            </motion.p>

            <div className="grid sm:grid-cols-2 gap-6 lg:gap-8 mt-14 max-w-3xl">
              <PropertyCard
                testid="card-punto-nima" delay={1.0} path="/punto-nima"
                image={RESORT.exteriorDay} tag="San José del Cabo" name="Punto Nima"
                location="2 Bed · 2 Bath · Rooftop Pool" cta="Explore & Book"
                onClick={() => nav("/punto-nima")} />
              <PropertyCard
                testid="card-punta-arena" delay={1.15} path="/casa-de-leon"
                image={CASA.hero} tag="El Tezal · Cabo San Lucas" name="Casa de León · Punta Arena"
                location="3 Bed · 3 Bath · Grand Terrace" cta="Explore & Book"
                onClick={() => nav("/casa-de-leon")} />
            </div>
          </div>
        </div>
      </div>

      {/* ---- MEET THE OWNER ---- */}
      <section id="owner" data-testid="landing-owner" className="px-6 lg:px-12 py-24 lg:py-32 max-w-[1400px] mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}>
            <div className="relative overflow-hidden rounded-sm">
              <img src={BRAND.florencia} alt="Florencia Derron" className="w-full aspect-[4/5] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}>
            <span className="overline text-[#B86B52]">{t("Meet Florencia")}</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">Florencia Derron</h2>
            <p className="text-[#5C5A56] font-light mt-4 leading-relaxed max-w-md">
              {t("Before you choose your escape, meet the person behind every Derron Luxury residence.")}
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              {["English", "Português", "Italiano", "Deutsch", "Español"].map((l) => (
                <span key={l} className="text-xs px-3 py-1.5 bg-[#8C9B90]/20 text-[#2A4759] rounded-sm">{t(l)}</span>
              ))}
            </div>
            <div className="space-y-4 mt-7 text-[#5C5A56] font-light leading-relaxed">
              <p>{t("Florencia has called beautiful Los Cabos home for nearly a decade and a half, where she invests in real estate and helps clients find their dream home or the perfect vacation property. Originally from Argentina and with three decades of international living experience, she naturally connects with people from different cultures and speaks Spanish, English, Portuguese, Italian, and German.")}</p>
              <p>{t("With a background in marketing, sales, coaching, and real estate, Florencia offers her clients a warm, professional, and highly personalized experience. Passionate about traveling, discovering new cities, and experiencing each destination like a local, she especially enjoys home exchanges and connecting with travelers from around the world.")}</p>
              <p>{t("When she is not working, Florencia enjoys playing the cello, attending concerts, relaxing by the beach, staying active, discovering great food, and finding special local places wherever she travels.")}</p>
              <p>{t("As a host, Florencia is warm, attentive, and genuinely committed to making her guests feel at home. She enjoys sharing personalized recommendations, helping guests discover the best places to visit, assisting with the language when needed, and being available throughout their stay. From before arrival to the end of the trip, she is there to help make every stay comfortable, effortless, and memorable.")}</p>
            </div>
            <p className="mt-7 font-display text-xl italic text-[#1A1918]">{t("Your host throughout the Derron Luxury Collection.")}</p>
            <a href="mailto:bookings@derronluxury.com?subject=Derron%20Luxury%20inquiry"
              data-testid="landing-owner-email"
              className="inline-flex items-center gap-2.5 mt-8 px-7 py-3.5 bg-[#2A4759] text-white text-sm tracking-wide rounded-sm hover:bg-[#1e3644] transition-colors">
              <Mail className="w-4 h-4" strokeWidth={1.5} /> {t("Email Florencia")}
            </a>
          </motion.div>
        </div>
      </section>

      {/* ---- FOOTER ---- */}
      <footer className="px-6 lg:px-12 py-8 border-t border-[#1A1918]/10 text-[#5C5A56] text-xs tracking-wide text-center">
        © {new Date().getFullYear()} Derron Luxury · Punto Nima & Punta Arena · Baja California Sur, México
      </footer>
    </div>
  );
}
